import React from 'react'
import clsx from 'clsx'
import { ProgressIndicator as ProgressIndicatorStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { CommonProps } from '../helperTypes'

type Props = {
  /** callback function that is called with index of clicked step */
  onStepClick?: (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => void
  /**
   * pass 'vertical' to show progress indicators vertically
   * @default "horizontal"
   */
  orientation?: 'horizontal' | 'vertical' | undefined
  /** index of current selected step */
  selected?: number
  /** The total number of steps */
  total: number
}

export type ProgressIndicatorProps = Props & CommonProps

const useStyles = createUseStyles(ProgressIndicatorStyles, 'progressindicator')

const ProgressIndicator = ({
  className,
  total,
  selected = 0,
  orientation = 'horizontal',
  onStepClick,
  style,
  ...rest
}: ProgressIndicatorProps) => {
  const classes = useStyles()
  const maxStepsOnScreen = 7
  const halfSteps = Math.floor(maxStepsOnScreen / 2)
  const hasOverflow = total > maxStepsOnScreen
  const overflowLeft = hasOverflow && selected > halfSteps
  const overflowRight = hasOverflow && total - (selected + 1) > halfSteps

  // compute offset translation
  const offset = overflowLeft ? Math.min(total - maxStepsOnScreen, selected - halfSteps) : 0
  const offsetStyle = {
    transform: orientation === 'horizontal' ? `translate(${-offset * 20}px, 0)` : `translate(0, ${-offset * 20}px)`,
  }

  return (
    <div className={classes.container} style={style}>
      <div
        className={clsx(classes.root, className, orientation === 'vertical' && classes._vertical)}
        role='tablist'
        style={offsetStyle}
        {...rest}
      >
        {Array(total)
          .fill(undefined)
          .map((step: undefined, index: number) => {
            return (
              <button
                role='tab'
                aria-label={`step ${index}`}
                key={'progress-indicator-step' + index}
                onClick={(evt) => onStepClick && onStepClick(evt, index)}
                className={clsx(
                  classes.step,
                  index === selected && classes.step_selected,
                  overflowLeft && index <= offset && classes.step_end,
                  overflowLeft && index === offset + 1 && classes.step_beforeend,
                  overflowRight && index === offset + maxStepsOnScreen - 2 && classes.step_beforeend,
                  overflowRight && index >= offset + maxStepsOnScreen - 1 && classes.step_end,
                )}
              />
            )
          })}
      </div>
    </div>
  )
}

export default ProgressIndicator
